import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Navigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  getAppEntryUrl,
  getAuthEntryUrl,
  getAccessFlowLock,
  lockAccessFlow,
  type AccessFlow,
} from "@/lib/access-flow";
import { signOutInvalidSession } from "@/lib/signout-invalid-session";
import { toast } from "sonner";
import { traceRouteEvent } from "@/lib/route-debug-trace";
import NotFound from "@/pages/NotFound";
import AdminAAL2Gate from "@/components/AdminAAL2Gate";
import AdminShortcutDeviceGate from "@/components/AdminShortcutDeviceGate";
import { authorizeRegisteredAccount } from "@/lib/auth/registeredAccount";

type GuardCacheValue = {
  allowed: boolean;
  denied: boolean;
  redirectTo: string | null;
};

const guardCache = new Map<string, GuardCacheValue>();
const getGuardCacheKey = (userId: string | undefined, mode: RoleGuardMode) =>
  userId ? `${userId}:${mode}` : null;

const ROLE_GUARD_TIMEOUT_MS = 5000;

/**
 * Registra tentativa de acesso negado à área administrativa.
 * Deduplica por sessão+rota+motivo para evitar flood em re-renders.
 */
const loggedAdminDenials = new Set<string>();
const logAdminAccessDenied = (reason: string, route: string, userId?: string | null) => {
  const key = `${userId ?? "anon"}:${route}:${reason}`;
  if (loggedAdminDenials.has(key)) return;
  loggedAdminDenials.add(key);
  try {
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : null;
    void supabase.rpc("log_admin_access_denied", {
      _reason: reason,
      _route: route,
      _user_agent: userAgent,
    });
  } catch {
    // best-effort; nunca quebrar a UI por causa do log
  }
};

const withTimeout = async <T,>(promise: PromiseLike<T>, fallback: T, timeoutMs = ROLE_GUARD_TIMEOUT_MS): Promise<T> => {
  let timeoutId: number | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((resolve) => {
        timeoutId = window.setTimeout(() => resolve(fallback), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
  }
};

export type RoleGuardMode = "user" | "venue" | "admin";

interface RoleGuardProps {
  mode: RoleGuardMode;
  children: React.ReactNode;
}

/**
 * Guard único parametrizado para os três fluxos (user/venue/admin).
 * Substitui ProtectedRoute, ProtectedVenueRoute e ProtectedAdminRoute,
 * consolidando lógica de verificação de papel, lock de fluxo cruzado e
 * camuflagem (admin = 404, demais = toast + redirect).
 */
const RoleGuard = React.forwardRef<HTMLDivElement, RoleGuardProps>(
  ({ mode, children }, _ref) => {
    const { user, loading } = useAuth();
    const location = useLocation();
    const queryClient = useQueryClient();
    const initialCacheKey = getGuardCacheKey(user?.id, mode);
    const activeGuardKeyRef = React.useRef<string | null>(initialCacheKey);
    const initialCache = initialCacheKey ? guardCache.get(initialCacheKey) : undefined;
    const [roleChecked, setRoleChecked] = useState(Boolean(initialCache));
    const [redirectTo, setRedirectTo] = useState<string | null>(initialCache?.redirectTo ?? null);
    const [denied, setDenied] = useState(initialCache?.denied ?? false);
    const [allowed, setAllowed] = useState(initialCache?.allowed ?? false);
    const appEntryUrl = getAppEntryUrl(
      `${location.pathname}${location.search}${location.hash}`
    );
    const userAuthEntryUrl = getAuthEntryUrl(
      "user",
      `${location.pathname}${location.search}${location.hash}`
    );

    useEffect(() => {
      if (loading) return;
      const nextKey = getGuardCacheKey(user?.id, mode);
      if (activeGuardKeyRef.current === nextKey) return;
      activeGuardKeyRef.current = nextKey;
      const cached = nextKey ? guardCache.get(nextKey) : undefined;
      setRoleChecked(Boolean(cached));
      setRedirectTo(cached?.redirectTo ?? null);
      setDenied(cached?.denied ?? false);
      setAllowed(cached?.allowed ?? false);
    }, [user?.id, mode, loading]);

    useEffect(() => {
      if (loading) return;

      if (!user) {
        if (mode === "admin") {
          // Camuflagem: 404 silencioso para visitantes.
          logAdminAccessDenied("no_session", `${location.pathname}${location.search}`, null);
          setDenied(true);
          setRoleChecked(true);
        } else {
          setRoleChecked(false);
        }
        return;
      }

      let cancelled = false;
      const route = `${location.pathname}${location.search}`;
      const check = async () => {
        try {
          await authorizeRegisteredAccount(supabase, mode, { adminMfaBootstrap: mode === "admin" });
          if (mode === "admin") {
            const data = await withTimeout(
              supabase.rpc("admin_bootstrap_identity").then((res) => !res.error && res.data === true),
              false
            );
          if (cancelled) return;

          const adminFlag = data;
          const lockedFlow = getAccessFlowLock();

          if (!adminFlag) {
            logAdminAccessDenied("not_admin", route, user.id);
            setDenied(true);
            setRoleChecked(true);
            return;
          }
          if (lockedFlow && lockedFlow !== "admin") {
            logAdminAccessDenied("cross_flow_lock", route, user.id);
            await signOutInvalidSession(queryClient, {
              userId: user.id,
              guardMode: "admin",
              lockedFlow,
              actualRoles: { admin: adminFlag },
              route,
              reason: "cross_flow_lock",
            });
            setDenied(true);
            setRoleChecked(true);
            return;
          }
          lockAccessFlow("admin");
          guardCache.set(`${user.id}:admin`, { allowed: true, denied: false, redirectTo: null });
          setAllowed(true);
          setRoleChecked(true);
          return;
        }

        if (mode === "venue") {
          const data = await withTimeout(
            supabase.rpc("has_role", {
              _user_id: user.id,
              _role: "venue_owner" as const,
            }).then((res) => !!res.data),
            false
          );
          if (cancelled) return;

          const ownerFlag = data;
          const lockedFlow = getAccessFlowLock();

          if (!ownerFlag) {
            // Sessão preservada: sem papel `venue_owner`, redirecionamos ao
            // onboarding de estabelecimento (`/venue-request`) em vez de
            // deslogar. Um `has_role` que retorne false por timeout/erro
            // transitório NÃO deve custar a sessão do usuário. Cross-flow
            // lock (venue_owner em fluxo user/admin) segue tratado abaixo.
            if (lockedFlow && lockedFlow !== "venue") {
              toast.error("Saia do fluxo atual e entre como estabelecimento.");
              await signOutInvalidSession(queryClient, {
                userId: user.id,
                guardMode: "venue",
                lockedFlow,
                actualRoles: { venue_owner: ownerFlag },
                route,
                reason: "cross_flow_lock",
              });
              setRedirectTo(appEntryUrl);
              setRoleChecked(true);
              return;
            }
            toast.error("Esta área é exclusiva para estabelecimentos.");
            // NÃO cacheamos este estado: o papel `venue_owner` pode ser
            // concedido logo após o onboarding em /venue-request, e o cache
            // impediria uma nova checagem quando o usuário voltasse.
            setRedirectTo("/venue-request");
            setRoleChecked(true);
            return;
          }
          if (lockedFlow && lockedFlow !== "venue") {
            toast.error("Saia do fluxo atual e entre como estabelecimento.");
            await signOutInvalidSession(queryClient, {
              userId: user.id,
              guardMode: "venue",
              lockedFlow,
              actualRoles: { venue_owner: ownerFlag },
              route,
              reason: "cross_flow_lock",
            });
            setRedirectTo(appEntryUrl);
            setRoleChecked(true);
            return;
          }
          lockAccessFlow("venue");
          guardCache.set(`${user.id}:venue`, { allowed: true, denied: false, redirectTo: null });
          setAllowed(true);
          setRoleChecked(true);
          return;
        }

        // mode === "user"
        const [isAdmin, isVenueOwner] = await withTimeout(
          Promise.all([
            supabase.rpc("has_role", { _user_id: user.id, _role: "admin" as const }),
            supabase.rpc("has_role", { _user_id: user.id, _role: "venue_owner" as const }),
          ]).then(([adminRes, ownerRes]) => [!!adminRes.data, !!ownerRes.data] as const),
          [false, false] as const
        );
        if (cancelled) return;

        const lockedFlow = getAccessFlowLock();
        const actualRoles = { admin: isAdmin, venue_owner: isVenueOwner };

        const handleCrossFlow = async (expected: AccessFlow, msg: string, fallback: string) => {
          if (lockedFlow && lockedFlow !== expected) {
            toast.error(msg);
            await signOutInvalidSession(queryClient, {
              userId: user.id,
              guardMode: "user",
              lockedFlow,
              actualRoles,
              route,
              reason: "cross_flow_lock",
            });
            setRedirectTo(appEntryUrl);
          } else {
            lockAccessFlow(expected);
            setRedirectTo(fallback);
          }
        };

        if (isAdmin) {
          await handleCrossFlow(
            "admin",
            "Esta conta é de administrador. Faça login pelo fluxo correto.",
            "/admin"
          );
        } else if (isVenueOwner) {
          await handleCrossFlow(
            "venue",
            "Esta conta é de estabelecimento. Faça login pelo fluxo correto.",
            "/venue-management"
          );
        } else {
          if (lockedFlow && lockedFlow !== "user") {
            toast.error("Esta conta é de usuário. Faça login pelo fluxo correto.");
            await signOutInvalidSession(queryClient, {
              userId: user.id,
              guardMode: "user",
              lockedFlow,
              actualRoles,
              route,
              reason: "cross_flow_lock",
            });
            setRedirectTo(appEntryUrl);
          } else {
            lockAccessFlow("user");
            guardCache.set(`${user.id}:user`, { allowed: true, denied: false, redirectTo: null });
            setRedirectTo(null);
            setAllowed(true);
          }
        }
        setRoleChecked(true);
        } catch {
          if (cancelled) return;
          setAllowed(false);
          guardCache.delete(`${user.id}:${mode}`);
          await signOutInvalidSession(queryClient, {
            userId: user.id, guardMode: mode, lockedFlow: getAccessFlowLock(),
            actualRoles: {}, route, reason: "role_mismatch",
          });
          if (mode === "admin") {
            logAdminAccessDenied("role_check_error", route, user.id);
            setDenied(true);
          } else setRedirectTo(appEntryUrl);
          setRoleChecked(true);
        }
      };

      check();

      const onVisible = () => {
        if (document.visibilityState === "visible") check();
      };
      document.addEventListener("visibilitychange", onVisible);
      window.addEventListener("focus", onVisible);
      return () => {
        cancelled = true;
        document.removeEventListener("visibilitychange", onVisible);
        window.removeEventListener("focus", onVisible);
      };
    }, [user, loading, appEntryUrl, mode]);

    const activeGuardKey = getGuardCacheKey(user?.id, mode);
    const keyMismatch = activeGuardKeyRef.current !== activeGuardKey;
    const renderedState = keyMismatch
      ? "null:key-mismatch"
      : loading
        ? "null:auth-loading"
        : user && !roleChecked
          ? "null:role-pending"
          : mode === "admin" && (!roleChecked || denied || !allowed)
            ? denied || !allowed ? "not-found:admin-denied" : "null:admin-pending"
            : !user
              ? "redirect:no-user"
              : redirectTo
                ? "redirect:role"
                : mode === "venue" && !allowed
                  ? "redirect:venue-not-allowed"
                  : "children:allowed";

    React.useEffect(() => {
      traceRouteEvent("RoleGuard:state", `${location.pathname}${location.search}`, {
        mode,
        loading,
        roleChecked,
        user: user ? "present" : "none",
        allowed,
        denied,
        redirectTo: redirectTo ?? null,
        keyMismatch,
        renderedState,
      });
    }, [allowed, denied, keyMismatch, loading, location.pathname, location.search, mode, redirectTo, renderedState, roleChecked, user]);

    // Telemetria de regressão: dispara um warning se alguma vez a árvore
    // de decisão produzir um Navigate/NotFound enquanto `loading` ainda é
    // true. Hoje os branches abaixo ficam atrás de `if (loading) return`,
    // mas mantemos esta sentinela para travar regressões futuras (ex.: se
    // alguém remover o gate de loading sem perceber o efeito em cascata).
    const reportPrematureRedirect = (decision: string, target: string | null) => {
      if (!loading) return;
      const route = `${location.pathname}${location.search}`;
      traceRouteEvent("RoleGuard:premature-redirect", route, {
        mode,
        decision,
        target,
        user: user ? "present" : "none",
        roleChecked,
        allowed,
        denied,
      });
      // eslint-disable-next-line no-console
      console.warn(
        "[RoleGuard] redirect decidido com loading=true",
        { mode, decision, target, route }
      );
      try {
        void supabase.rpc("log_admin_access_denied", {
          _reason: `premature_redirect:${decision}`,
          _route: route,
          _user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      } catch {
        /* best-effort */
      }
    };

    // Render-gate hardening (Frente 1):
    // NUNCA renderizar children enquanto não houver confirmação positiva
    // (`allowed === true`) para o `user` atual. Anteriormente, o ramo
    // `mode === "user"` liberava children eagerly durante o role check
    // pendente, criando uma janela em que JWT adulterado/expirado vazava
    // UI protegida por alguns ms — exatamente o gap que `tampered-jwt-storage`
    // exercita.
    if (keyMismatch) return null;
    if (loading) {
      return mode !== "admin" && user && allowed ? <>{children}</> : null;
    }
    if (user && !roleChecked) {
      return mode !== "admin" && allowed ? <>{children}</> : null;
    }

    if (mode === "admin") {
      if (!roleChecked) return null;
      if (denied || !allowed) {
        // Camuflagem de rota administrativa: qualquer sessão sem o papel
        // `admin` (ou visitante) recebe 404 idêntico ao NotFound global.
        // Nunca redirecionamos para `/admin/login`, pois isso vazaria a
        // existência da área. O login administrativo tem entrada própria
        // e explícita e não depende de descoberta pela rota protegida.
        return <NotFound />;
      }
      // Bootstrap never bypasses MFA, including tests and revalidation.
      return (
        <AdminShortcutDeviceGate>
          <AdminAAL2Gate>{children}</AdminAAL2Gate>
        </AdminShortcutDeviceGate>
      );
    }

    if (!user) {
      const unauthenticatedTarget = mode === "user" ? userAuthEntryUrl : appEntryUrl;
      reportPrematureRedirect("no-user", unauthenticatedTarget);
      return <Navigate to={unauthenticatedTarget} replace />;
    }
    if (redirectTo) {
      reportPrematureRedirect("role-redirect", redirectTo);
      return <Navigate to={redirectTo} replace />;
    }
    if (mode === "venue" && !allowed) {
      reportPrematureRedirect("venue-not-allowed", appEntryUrl);
      return <Navigate to={appEntryUrl} replace />;
    }
    return <>{children}</>;

  }
);

RoleGuard.displayName = "RoleGuard";

export default RoleGuard;
